import { create } from 'zustand'
import { DEFAULT_SETTINGS, readSettings, SETTINGS_KEY } from './settingsPersist'
import type { PersistedSettings } from './settingsPersist'
import { write } from '@/lib/storage'
import type { Category, EvolutionTier } from '@/milestones/types'

interface SettingsState extends PersistedSettings {
  setTheme: (t: PersistedSettings['theme']) => void
  setLocale: (l: PersistedSettings['locale']) => void
  setSlideIntervalSec: (s: number | null) => void
  setSoundEnabled: (b: boolean) => void
  setSoundVolume: (v: number) => void
  setQuietHours: (from: number, to: number) => void
  setPerSlideOverride: (
    id: keyof PersistedSettings['perSlideOverrides'],
    sec: number | null,
  ) => void
  setDebugTier: (category: Category, tier: EvolutionTier | null) => void
  clearDebugTiers: () => void
  resetAll: () => void
}

function persist(s: PersistedSettings) {
  write(SETTINGS_KEY, s)
}

const initial: PersistedSettings = { ...DEFAULT_SETTINGS, ...(readSettings() ?? {}) }

export const useSettingsStore = create<SettingsState>((set) => ({
  ...initial,
  setTheme(theme) {
    set({ theme })
    persistCurrent({ theme })
  },
  setLocale(locale) {
    set({ locale })
    persistCurrent({ locale })
  },
  setSlideIntervalSec(slideIntervalSec) {
    set({ slideIntervalSec })
    persistCurrent({ slideIntervalSec })
  },
  setSoundEnabled(soundEnabled) {
    set({ soundEnabled })
    persistCurrent({ soundEnabled })
  },
  setSoundVolume(soundVolume) {
    set({ soundVolume })
    persistCurrent({ soundVolume })
  },
  setQuietHours(quietHoursFrom, quietHoursTo) {
    set({ quietHoursFrom, quietHoursTo })
    persistCurrent({ quietHoursFrom, quietHoursTo })
  },
  setPerSlideOverride(id, sec) {
    set((state) => {
      const next = { ...state.perSlideOverrides }
      if (sec === null) {
        delete next[id]
      } else {
        next[id] = sec
      }
      const updated = { perSlideOverrides: next }
      persistCurrent(updated)
      return updated
    })
  },
  setDebugTier(category, tier) {
    set((state) => {
      const next = { ...state.debugTiers }
      if (tier === null) {
        delete next[category]
      } else {
        next[category] = tier
      }
      const updated = { debugTiers: next }
      persistCurrent(updated)
      return updated
    })
  },
  clearDebugTiers() {
    set({ debugTiers: {} })
    persistCurrent({ debugTiers: {} })
  },
  resetAll() {
    set({ ...DEFAULT_SETTINGS })
    persist(DEFAULT_SETTINGS)
  },
}))

function persistCurrent(patch: Partial<PersistedSettings>) {
  const cur = useSettingsStore.getState()
  const next: PersistedSettings = {
    theme: cur.theme,
    locale: cur.locale,
    slideIntervalSec: cur.slideIntervalSec,
    soundEnabled: cur.soundEnabled,
    soundVolume: cur.soundVolume,
    quietHoursFrom: cur.quietHoursFrom,
    quietHoursTo: cur.quietHoursTo,
    perSlideOverrides: cur.perSlideOverrides,
    debugTiers: cur.debugTiers,
    ...patch,
  }
  persist(next)
}
